import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';

import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import BaseModalComponent from '../BaseModal.component';
import InputComponent, { StyledInput } from '../../Input/Input.component';
import { useTranslation } from 'react-i18next';
import ButtonComponent, { StyledButton } from '../../Button.component';
import styled from 'styled-components';
import {
  maxLength,
  minLength,
  required,
} from '../../../services/validator.service';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import SelectComponent, {
  SelectOptionProps,
  StyledSelectWrapper,
} from '../../Input/Select.component';
import { StatusColor } from '../../../constants/statusColors.constant';
import { Status } from '../../../redux/ControlPanel/StatusControl/StatusControl.type';
import UserBadgeComponent from '../../UserBadge.component';
import useRegionHook from '../../../hooks/useRegion.hook';
import { Region } from '../../../redux/ControlPanel/RegionControl/RegionControl.type';
import useEventsHook from '../../../hooks/useEvents.hook';
import BadgeComponent from '../../Badge.component';
import { ControlSectionTypes } from '../../../redux/ControlPanel/ControlPanelList/ControlPanelList.type';
import { ListControlSectionTypes } from '../../../views/pages/ControlPanel/ControlPanel.types';
import useFormHook from '../../../hooks/useForm.hook';
import { ColorPicker } from '../../Input/ColorPicker/ColorPicker.component';
import { getUserSelfThunk } from '../../../redux/User/UserThunk';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import DatepickerModalComponent from '../DatepickerModal.component';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../../constants/date.contant';

const StyledSelectContainer = styled.div`
  max-width: 400px;
`;

const StyledSelectComponent = styled(SelectComponent)`
  max-height: 150px;
`;

export interface MainGroupSelectOption extends SelectOptionProps {
  eventId: number;
}

export interface ControlPanelItemFormValues {
  name: string;
  id?: number;
  mainGroup?: MainGroupSelectOption;
  tab?: string;
  code?: string | null;
  color?: string;
  textColor?: string;
  regions: Array<SelectOptionProps>;
  hideCandidatesBefore?: number | null;
  initialHideCandidatesBefore?: number | null;
}

export interface ControlPanelItemFormValuesWithId
  extends ControlPanelItemFormValues {
  id: number;
}

interface Props extends React.AllHTMLAttributes<HTMLDivElement> {
  sectionType: ControlSectionTypes;
  sectionItem?: { id: number; [key: string]: any };
  createNewSectionItem?: Function;
  editSectionItem?: Function;
  deleteSectionItem?: Function;
  editable?: boolean;
  deletable?: boolean;
  checkUnique?: (value: string) => boolean;
  canManageCandidateVisibility?: boolean;
  onSaveVisibility?: (
    employeeId: number,
    hideCandidatesBefore: number | null,
    initialHideCandidatesBefore: number | null,
  ) => Promise<void>;
}

const ControlCreateEditModal = ({
  sectionType,
  sectionItem,
  createNewSectionItem,
  editSectionItem,
  deleteSectionItem,
  editable,
  deletable,
  checkUnique,
  canManageCandidateVisibility = false,
  onSaveVisibility,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { closeModal, openModal } = useModalManager();
  const dateNow = dayjs().startOf('day').valueOf();
  const { getSelectList: getRegionList } = useRegionHook();
  const { getSelectList: getMainGroupsList } = useEventsHook();

  const { form, validate, setFormValue, registerField, handleFormChange } =
    useFormHook({
      name: '',
      regions: [],
      ...([
        ControlSectionTypes.STATUSES,
        ControlSectionTypes.INTERNSHIP_STATUSES,
      ].includes(sectionType) && {
        color: StatusColor.GREY,
        textColor: StatusColor.GREY,
      }),
    } as ControlPanelItemFormValues);

  const [isUniqueName, setIsUniqueName] = useState(true);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
  const [removedRegions, setRemovedRegions] = useState<Array<string | number>>(
    [],
  );
  const dispatch = useAppDispatch();

  const isHRSection = useMemo(
    () => sectionType === ControlSectionTypes.HRS,
    [sectionType],
  );

  const canEditRegions = isHRSection ? !!editable : !!editable;
  const canSave = !!editable || (canManageCandidateVisibility && isHRSection);

  useEffect(() => {
    switch (sectionType) {
      case ControlSectionTypes.HRS: {
        const { isSame, removedItems } = checkRegionsSameness(
          sectionItem?.regions,
          form.regions,
        );
        const visibilityChanged =
          canManageCandidateVisibility &&
          form.hideCandidatesBefore !== form.initialHideCandidatesBefore;
        setIsSaveButtonDisabled(isSame && !visibilityChanged);
        setRemovedRegions(removedItems);
        break;
      }

      default: {
        const unique = checkUnique ? checkUnique?.(form.name) : true;
        setIsUniqueName(!!unique);
        setIsSaveButtonDisabled(!unique);
        break;
      }
    }
  }, [form, sectionType]);

  useLayoutEffect(() => {
    if (sectionItem) {
      setFormValue({
        id: sectionItem.id,
        name: sectionItem.name,
        mainGroup:
          sectionType === ControlSectionTypes.STATUSES
            ? {
                value: (sectionItem as Status).event.code,
                label: (sectionItem as Status).event.name,
                eventId: (sectionItem as Status).event.id,
              }
            : undefined,
        tab:
          sectionType === ControlSectionTypes.STATUSES
            ? (sectionItem as Status).tab
            : undefined,
        code: (sectionItem as Region).code ?? undefined,
        color: (sectionItem as Status).color ?? undefined,
        textColor: (sectionItem as Status).textColor ?? undefined,
        regions: sectionItem.regions?.map((r: any) => ({
          value: r.id,
          label: r.name,
        })),
        hideCandidatesBefore: sectionItem.hideCandidatesBefore ?? null,
        initialHideCandidatesBefore: sectionItem.hideCandidatesBefore ?? null,
      });
    }
  }, [sectionItem]);

  const checkRegionsSameness = (
    currentRegions: { id: string | number }[],
    changedRegions: SelectOptionProps[],
  ) => {
    const removedItems = difference(
      currentRegions.map(cr => cr.id),
      changedRegions.map(chr => chr.value),
    );

    return {
      isSame:
        currentRegions.length === changedRegions.length &&
        isEmpty(removedItems),
      removedItems,
    };
  };

  const sectionTitle = useMemo(() => {
    switch (sectionType as ListControlSectionTypes) {
      case ControlSectionTypes.SOURCES:
        return 'source';
      case ControlSectionTypes.STATUSES:
        return 'status';
      case ControlSectionTypes.TAGS:
        return 'tag';
      case ControlSectionTypes.VACANCIES:
        return 'vacancy';
      case ControlSectionTypes.REGIONS:
        return 'region';
      case ControlSectionTypes.HRS:
        return 'hr';
      case ControlSectionTypes.INTERNSHIP_STATUSES:
        return 'internship-status';
    }
  }, [sectionType]);

  const handleCreateEdit = () => {
    if (!validate()) return;

    const createEdit = async () => {
      if (form.id) {
        if (canEditRegions) {
          editSectionItem && editSectionItem(form, sectionType);
        }
        if (
          isHRSection &&
          canManageCandidateVisibility &&
          onSaveVisibility &&
          form.id &&
          form.hideCandidatesBefore !== form.initialHideCandidatesBefore
        ) {
          await onSaveVisibility(
            form.id,
            form.hideCandidatesBefore ?? null,
            form.initialHideCandidatesBefore ?? null,
          );
        }
      } else {
        createNewSectionItem && createNewSectionItem(form, sectionType);
      }
      closeModal();

      const timer = setTimeout(() => {
        dispatch(getUserSelfThunk());
      }, 500);

      return () => clearTimeout(timer);
    };

    if (!isEmpty(removedRegions))
      openModal(ConfirmDeleteModal, {
        section: sectionTitle,
        confirmDelete: createEdit,
        customMessage: sectionItem?.regions
          ?.filter((r: { id: number; name: string }) =>
            removedRegions.includes(r.id),
          )
          ?.map((r: { id: number; name: string }) => r.name)
          ?.join(', '),
        handleClose: () => closeModal(),
      });
    else {
      createEdit();
    }
  };

  const handleDelete = () => {
    sectionItem &&
      deleteSectionItem &&
      openModal(ConfirmDeleteModal, {
        section: sectionTitle,
        confirmDelete: () => deleteSectionItem(sectionItem, sectionType),
        handleClose: () => closeModal(),
      });
  };

  const renderFooter = useMemo(
    () => (
      <StyledFooterButtonContainer>
        {form?.id && !isHRSection && deletable && !sectionItem?.isSystem && (
          <ButtonComponent
            className="delete-button"
            handleClick={handleDelete}
            customColor="#d14343"
          >
            {t('Delete')}
          </ButtonComponent>
        )}
        {canSave && (
          <ButtonComponent
            disabled={isSaveButtonDisabled}
            handleClick={handleCreateEdit}
            customColor="#206FE2"
          >
            {t(form?.id ? 'Save' : 'Create')}
          </ButtonComponent>
        )}
        <ButtonComponent handleClick={closeModal}>
          {t('Cancel')}
        </ButtonComponent>
      </StyledFooterButtonContainer>
    ),
    [handleCreateEdit],
  );

  return (
    <BaseModalComponent
      header={
        form?.id
          ? t(`controlModal.${sectionTitle}.Edit ${sectionTitle}`)
          : t(`controlModal.${sectionTitle}.New ${sectionTitle}`)
      }
      footer={renderFooter}
    >
      {!isHRSection ? (
        <>
          {sectionItem?.isSystem && (
            <StyledNote>
              {t('This is system source, field can not be changed')}
            </StyledNote>
          )}
          {!isUniqueName && (
            <StyledNote>
              {t(`${sectionTitle} name should be unique`)}
            </StyledNote>
          )}
          <StyledControlModalBody>
            <InputComponent
              {...registerField('name', {
                validations: [required, minLength(1), maxLength(255)],
              })}
              value={form.name}
              disabled={!editable || sectionItem?.isSystem}
              label={t('Title') + '*'}
              autoFocus
              maxLength={255}
              onChange={(name: string) =>
                handleFormChange({
                  name,
                  code:
                    sectionType === ControlSectionTypes.REGIONS
                      ? name?.toLocaleLowerCase()
                      : form.code,
                })
              }
            />
            {sectionType === ControlSectionTypes.INTERNSHIP_STATUSES && (
              <StyledStatusModalBody>
                <ColorPicker
                  {...registerField('color')}
                  disabled={!editable}
                  value={(form.color as StatusColor) ?? StatusColor.GREY}
                  onChangeColor={color => handleFormChange({ color })}
                  excludedColors={[StatusColor.BLACK, StatusColor.WHITE]}
                />

                <ColorPicker
                  {...registerField('textColor')}
                  disabled={!editable}
                  label={`Цвет текста`}
                  value={(form.textColor as StatusColor) ?? StatusColor.GREY}
                  onChangeColor={textColor => handleFormChange({ textColor })}
                />

                <StyledPreviewLabel>{t('Preview')}</StyledPreviewLabel>

                <StyledStatusBadge>
                  {!!form.name && (
                    <BadgeComponent
                      appearance="default"
                      color={
                        form.color && i18n.exists(`statusColor.${form.color}`)
                          ? `STATUSES_${form.color.toUpperCase()}`
                          : form.color ||
                            `STATUSES_${StatusColor.GREY.toUpperCase()}`
                      }
                      textColor={
                        form.textColor &&
                        i18n.exists(`statusColor.${form.textColor}`)
                          ? `STATUSES_${form.textColor.toUpperCase()}`
                          : form.textColor
                      }
                      text={form.name}
                    />
                  )}
                </StyledStatusBadge>
              </StyledStatusModalBody>
            )}
          </StyledControlModalBody>

          {sectionType === ControlSectionTypes.STATUSES ? (
            <StyledStatusModalBody>
              <SelectComponent
                {...registerField('mainGroup', {
                  validations: [required],
                })}
                value={form.mainGroup}
                disabled={!editable || sectionItem?.isSystem}
                label={t('Main group') + '*'}
                placeholder={t('Choose main group')}
                loadOptions={getMainGroupsList}
                onChangeOption={(mainGroup: any) =>
                  handleFormChange({ mainGroup })
                }
                isSearchable
                useMenuPortalTarget
              />

              <ColorPicker
                {...registerField('color')}
                value={(form.color as StatusColor) ?? StatusColor.GREY}
                disabled={!editable}
                onChangeColor={color => handleFormChange({ color })}
                excludedColors={[StatusColor.BLACK, StatusColor.WHITE]}
              />

              <ColorPicker
                {...registerField('textColor')}
                value={(form.textColor as StatusColor) ?? StatusColor.GREY}
                disabled={!editable}
                label={`Цвет текста`}
                onChangeColor={textColor => handleFormChange({ textColor })}
              />

              <StyledPreviewLabel>{t('Preview')}</StyledPreviewLabel>

              <StyledStatusBadge>
                {!!form.name && (
                  <BadgeComponent
                    appearance="default"
                    color={
                      form.color && i18n.exists(`statusColor.${form.color}`)
                        ? `STATUSES_${form.color.toUpperCase()}`
                        : form.color ||
                          `STATUSES_${StatusColor.GREY.toUpperCase()}`
                    }
                    textColor={
                      form.textColor &&
                      i18n.exists(`statusColor.${form.textColor}`)
                        ? `STATUSES_${form.textColor.toUpperCase()}`
                        : form.textColor
                    }
                    text={form.name}
                  />
                )}
              </StyledStatusBadge>
            </StyledStatusModalBody>
          ) : null}
        </>
      ) : (
        <>
          <StyledHRInfoWrapper>
            <div>
              {t(`controlModal.${sectionTitle}.Selected ${sectionTitle}`)}:
            </div>
            <UserBadgeComponent info={sectionItem as any} />
          </StyledHRInfoWrapper>

          <StyledSelectContainer>
            <StyledSelectComponent
              {...registerField('regions')}
              value={form.regions}
              disabled={!canEditRegions}
              label={t(`controlModal.${sectionTitle}.List of regions`)}
              placeholder={t('Choose regions')}
              isMulti
              useMenuPortalTarget
              loadOptions={(...props: any) => {
                return getRegionList(
                  //@ts-ignore
                  ...props,
                  sectionItem?.regions?.map((r: any) => r.id),
                );
              }}
              onChangeOption={(regions: any) => handleFormChange({ regions })}
            />
          </StyledSelectContainer>

          {canManageCandidateVisibility && (
            <StyledVisibilityField>
              <InputComponent
                value={
                  form.hideCandidatesBefore
                    ? dayjs(form.hideCandidatesBefore).format(DATE_FORMAT)
                    : ''
                }
                label={t('Candidates visible from date')}
                placeholder={t('All candidates are visible')}
                readonly
                disabled={!canManageCandidateVisibility}
                onClick={() =>
                  canManageCandidateVisibility &&
                  openModal(DatepickerModalComponent, {
                    withTime: false,
                    defaultDate: form.hideCandidatesBefore ?? dateNow,
                    handleChange: (date: number) =>
                      handleFormChange({ hideCandidatesBefore: date }),
                  })
                }
              />
              {form.hideCandidatesBefore && canManageCandidateVisibility && (
                <ButtonComponent
                  handleClick={() => handleFormChange({ hideCandidatesBefore: null })}
                >
                  {t('Reset visibility restriction')}
                </ButtonComponent>
              )}
              <StyledVisibilityHint>
                {t(
                  'HR will only see candidates created on or after the selected date. Older candidates will be hidden.',
                )}
              </StyledVisibilityHint>
            </StyledVisibilityField>
          )}
        </>
      )}
    </BaseModalComponent>
  );
};
export default ControlCreateEditModal;

export const StyledFooterButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 10px 0 0 0;
  gap: 10px;

  ${StyledButton} {
    min-width: 100px;
  }
`;

const StyledVisibilityField = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${StyledInput} {
    width: 100%;
    cursor: pointer;
  }
`;

const StyledVisibilityHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`;

const StyledControlModalBody = styled.div`
  width: 600px;

  ${StyledInput} {
    width: 100%;
    height: 30px;
    font-size: inherit;
    font-weight: normal;
  }
`;

const StyledStatusModalBody = styled.div`
  width: 600px;
  display: flex;
  flex-flow: column;
  gap: 10px;
  margin: 20px 0;

  ${StyledSelectWrapper} {
  }
`;

const StyledPreviewLabel = styled.div`
  font-weight: 500;
  font-size: 14px;
`;

const StyledHRInfoWrapper = styled.div`
  display: flex;
  margin-bottom: 10px;

  > div:first-child {
    margin-right: 10px;
    font-weight: 500;
  }
`;

const StyledStatusBadge = styled.div`
  > div {
    width: fit-content;
    overflow-wrap: anywhere;
  }
`;

const StyledNote = styled.div`
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.STATUSES_RED};
`;
